/**
 * Scroll-triggered fade and rise.
 *
 * `once: true` so a section settles the first time it is reached and never
 * animates again on scroll-back — a page that keeps re-fading as you move up
 * and down reads as a glitch rather than a flourish.
 *
 * Reduced motion returns the children in a plain div. Not a shorter
 * animation, not a fade without the rise: the OS setting exists because
 * motion makes some people ill, and honouring it halfway is not honouring it.
 */

import { motion, useReducedMotion } from 'framer-motion';
import type { ReactNode } from 'react';

export function Reveal({
  children,
  delay = 0,
  y = 24,
  className = '',
}: {
  children: ReactNode;
  /** Seconds. Stagger siblings sparingly — it reads as lag past ~0.1s. */
  delay?: number;
  /** Px travelled. */
  y?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.45, delay, ease: [0.22, 0.61, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default Reveal;
